<template>
  <div class="relative w-full max-w-2xl mx-auto">
    <Combobox v-model="selectedValue" @update:model-value="handleSelection">
      <div class="relative">
        <ComboboxInput
          ref="searchInput"
          :value="searchQuery"
          @change="handleInputChange"
          @input="handleInput"
          :placeholder="placeholder"
          class="w-full px-4 sm:px-6 py-3 sm:py-4 pr-12 sm:pr-16 text-base sm:text-lg border-0 rounded-xl sm:rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 transition-all duration-200"
        />
        
        <!-- Search Icon / Loading Spinner / Clear Button -->
        <div class="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          <!-- Clear Button -->
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
          
          <!-- Search Icon / Loading Spinner -->
          <div v-if="!searchQuery" class="p-1.5">
            <MagnifyingGlassIcon class="w-5 h-5 text-gray-400" />
          </div>
          
          <!-- Loading Spinner (shown when loading prop is true) -->
          <div v-if="loading" class="p-1.5">
            <svg class="animate-spin h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>

        <!-- Suggestions Dropdown -->
        <TransitionRoot
          leave="transition ease-in duration-100"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <ComboboxOptions
            v-if="filteredSuggestions.length > 0"
            class="absolute z-10 mt-2 w-full max-h-60 overflow-auto rounded-xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div class="py-1">
              <ComboboxOption
                v-for="suggestion in filteredSuggestions"
                :key="suggestion"
                v-slot="{ active, selected }"
                :value="suggestion"
                as="template"
              >
                <li
                  :class="[
                    'relative cursor-pointer select-none py-3 sm:py-2 pl-10 pr-4 text-base sm:text-sm',
                    active ? 'bg-purple-100 dark:bg-gray-700 text-purple-900 dark:text-white' : 'text-gray-900 dark:text-gray-100',
                  ]"
                >
                  <span :class="['block truncate', selected ? 'font-medium' : 'font-normal']">
                    {{ suggestion }}
                  </span>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-600 dark:text-purple-400"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </div>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
  </div>
</template>

<script>
import { 
  Combobox, 
  ComboboxInput, 
  ComboboxOptions, 
  ComboboxOption,
  TransitionRoot 
} from '@headlessui/vue'
import { 
  MagnifyingGlassIcon, 
  XMarkIcon, 
  CheckIcon 
} from '@heroicons/vue/24/outline'

export default {
  name: 'SearchBar',
  components: {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    TransitionRoot,
    MagnifyingGlassIcon,
    XMarkIcon,
    CheckIcon
  },
  
  props: {
    placeholder: {
      type: String,
      default: 'Search wallpapers...'
    },
    loading: {
      type: Boolean,
      default: false
    },
    suggestions: {
      type: Array,
      default: () => [
        'nature',
        'abstract',
        'space',
        'city',
        'mountain',
        'ocean',
        'sunset',
        'minimalist',
        'landscape',
        'technology',
        'animals',
        'flowers',
        'architecture',
        'cars',
        'fantasy'
      ]
    },
    debounceMs: {
      type: Number,
      default: 300
    }
  },
  
  emits: ['search', 'clear', 'update:modelValue', 'suggestion-select'],
  
  data() {
    return {
      searchQuery: '',
      selectedValue: '',
      debounceTimer: null,
      isFocused: false
    };
  },
  
  computed: {
    filteredSuggestions() {
      if (!this.searchQuery.trim()) {
        return this.suggestions.slice(0, 8); // Show top 8 suggestions when empty
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.suggestions
        .filter(suggestion => 
          suggestion.toLowerCase().includes(query) && 
          suggestion.toLowerCase() !== query
        )
        .slice(0, 6); // Show max 6 filtered suggestions
    }
  },
  
  methods: {
    handleInputChange(event) {
      this.searchQuery = event.target.value;
      
      // Clear any existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Set new timer
      this.debounceTimer = setTimeout(() => {
        if (this.searchQuery.trim()) {
          this.$emit('search', this.searchQuery.trim());
        }
      }, this.debounceMs);
    },
    
    handleInput() {
      // Update v-model
      this.$emit('update:modelValue', this.searchQuery);
      
      // Clear suggestions when input is cleared
      if (!this.searchQuery) {
        this.$emit('clear');
      }
    },
    
    handleSelection(value) {
      this.searchQuery = value;
      this.selectedValue = value;
      this.$emit('update:modelValue', value);
      this.$emit('suggestion-select', value);
      
      // Focus the input after selection
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.selectedValue = '';
      this.$emit('update:modelValue', '');
      this.$emit('clear');
      
      // Focus the input after clearing
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    
    focus() {
      this.$refs.searchInput?.focus();
    }
  },
  
  unmounted() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  
  // Expose methods for parent components
  expose: ['focus', 'clearSearch']
};
</script>